import { useCallback, useEffect, useRef, useState } from "react";
import {
  COACH_MEMORY_CATEGORIES,
  COACH_MEMORY_CATEGORY_LABELS as CATEGORY_LABELS,
  COACH_MEMORY_CLASSES,
  COACH_MEMORY_CLASS_LABELS as CLASS_LABELS,
  COACH_MEMORY_FACT_TYPE_LABELS as FACT_TYPE_LABELS,
  CURRENT_MEMORY_CLASSES,
  defaultMemoryClass,
  type CoachMemory,
  type CoachMemoryCategory,
  type CoachMemoryClass,
  type CoachMemoryFactType,
  type CoachMemoryItem,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  confirmCoachMemoryItem,
  createCoachMemoryItem,
  fetchCoachMemory,
  ForbiddenError,
  retireCoachMemoryItem,
  reviseCoachMemoryItem,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  PatientMemoryRequests,
  RegisterMemoryRequest,
} from "./PatientMemoryRequests";
import {
  historyHeading,
  previousVersions,
  provenanceFields,
  retiredItems,
  temporaryContextFields,
} from "@/lib/memory-provenance";
import { useReadOnly } from "./SupportView";
import {
  memoryItemAnchor,
  memoryItemIdOfHash,
  PATIENT_MEMORY_ANCHOR,
} from "@/lib/coach-brief-anchors";
import {
  memoryAreaFacts,
  memoryAreaUnconfirmedLine,
  summarizeMemoryArea,
} from "@/lib/memory-area-summary";

// TES-745/746/747 (TES-818) — the coach's patient memory, finally readable.
//
// The backend has stored this since 7d660461 and no admin surface read it, so
// a coach could not see a single claim the system had recorded about her
// patient. This panel is that surface.
//
// Two things the UI must not soften, because they are the design:
//
//  1. ONE ROW PER CLAIM, NEVER OVERWRITTEN. "Rätta" is not an edit — the API
//     supersedes the old row and writes a new one, and "Ta bort" sets a
//     lifecycle rather than deleting. The copy says so out loud, because a
//     coach who believes she is overwriting will phrase corrections as if the
//     old wording disappears.
//
//  2. VERIFIED AND MERELY NOTED LOOK DIFFERENT. A-01 asks for a *controlled*
//     memory view: an `ai_observation` nobody has confirmed is a hypothesis
//     and is marked as one, a confirmed one carries the name of the human who
//     stood behind it. Presenting them alike is the one failure mode that
//     makes the whole panel untrustworthy.
//
// TES-1019 (Emelie, points 9 and 12) — every row shows its provenance: area,
// type, source, who conveyed it, who registered it (or that it was created
// automatically) and when (lib/memory-provenance). A corrected row offers
// its earlier versions, and "Ta bort" inactivates: the row moves to the
// inactivated list below, text and origin intact. A form source has no link
// to the original answer, because the admin has no route to a single
// submission; the form's name and the answer date stand in for it.
//
// TES-1192 (Emelie 2026-10-08) — the panel is grouped by the five classes:
// "Aktuellt" (the current-focus, temporary-context, agreement and follow-up
// classes) first, then the persistent class by area. A temporary context
// carries its period, impact and agreement; after its end date the server
// leaves it out of "Aktuellt" and lists it under "Historik" with its dates.
//
// TES-1211 — each area of the persistent class leads with a short summary
// derived from its rows (lib/memory-area-summary) and folds the rows away
// behind "Visa källor". The rows stay the one place a fact is shown in full
// and acted on; "Patienten i korthet" footnotes into them. A link to a row
// (#minne-<id>, from the brief, the top summary or an AI draft's "Rätta")
// unfolds its area first, so no existing link lands on a hidden row.
// "Aktuellt" is not folded: it is few rows and time-bound.

// A coach writing in this panel is making a note or recording an agreement.
// `ai_observation` is deliberately NOT offered: it means "a model said this",
// and a human typing it would make the confirmation flag meaningless.
const AUTHORABLE_FACT_TYPES: CoachMemoryFactType[] = [
  "patient_statement",
  "agreement",
];

const BTN =
  "rounded-md border border-surfaceMuted px-sm py-xs text-xs text-textSecondary hover:text-textPrimary disabled:opacity-50";
const BTN_PRIMARY =
  "rounded-md bg-brand px-md py-sm text-sm text-white hover:bg-brand/90 disabled:opacity-50";
const FIELD =
  "rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

export function PatientMemoryPanel({
  patientId,
  onChanged,
  correct,
}: {
  patientId: string;
  // TES-1074 — called after every successful write, so "Patienten i korthet"
  // (composed from the same rows) can refetch.
  onChanged?: () => void;
  // TES-1352 — open this entry's "Rätta" form, as asked from an AI draft's
  // "Underlag". A new `n` is a new request.
  correct?: { itemId: string; n: number } | null;
}) {
  const { claims, signOut } = useAuth();
  // TES-1040 — read-only for the admin: no adding, confirming, correcting
  // or removing (MemoryRow hides its own buttons).
  const readOnly = useReadOnly();
  const [memory, setMemory] = useState<CoachMemory | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 403 is not an error state. The routes are @Roles("coach", "doctor"), so a
  // 403 means the signed-in employee is simply not who this panel is for —
  // the panel disappears, the way the AI draft does in WorkItemCard.
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showEmptyAreas, setShowEmptyAreas] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [retiringId, setRetiringId] = useState<string | null>(null);
  // TES-1367 — bumped when a request is registered, so the requests list
  // refetches.
  const [requestsVersion, setRequestsVersion] = useState(0);
  // TES-1211 — the persistent-class areas whose rows are unfolded.
  const [openAreas, setOpenAreas] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const openAreaOf = useCallback(
    (itemId: string) => {
      const area = memory?.areas.find((a) =>
        a.items.some((i) => i.id === itemId),
      );
      if (!area) return false;
      setOpenAreas((s) =>
        s.has(area.category) ? s : new Set(s).add(area.category),
      );
      return true;
    },
    [memory],
  );
  // For the "Rätta" request below, which must not re-run on every refetch.
  const openAreaOfRef = useRef(openAreaOf);
  openAreaOfRef.current = openAreaOf;
  // The row the location hash points at. Followed once per hash, so a
  // refetch after a write does not scroll the page back to it.
  const [linkedId, setLinkedId] = useState<string | null>(() =>
    typeof window === "undefined"
      ? null
      : memoryItemIdOfHash(window.location.hash),
  );
  const followedLink = useRef<string | null>(null);

  const handleError = useCallback(
    (e: unknown, fallback: string) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      if (e instanceof ForbiddenError) {
        setForbidden(true);
        return;
      }
      // TES-811 — the API answers a lost race with `coach_memory_not_active`;
      // a raw snake_case code told the coach nothing about what to do next.
      setError(e instanceof Error ? errorText(e.message) : fallback);
    },
    [signOut],
  );

  const reload = useCallback(async () => {
    try {
      setMemory(await fetchCoachMemory(patientId));
      setError(null);
    } catch (e) {
      handleError(e, "Kunde inte hämta patientbilden.");
    }
  }, [patientId, handleError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // The rows may arrive after the request does; the form opens once the
  // entry is among them, and the panel brings it into view.
  const correctId = correct?.itemId ?? null;
  const correctN = correct?.n ?? 0;
  const correctLoaded =
    correctId !== null &&
    (memory?.areas ?? []).some((a) => a.items.some((i) => i.id === correctId));
  useEffect(() => {
    const onHash = () => {
      followedLink.current = null;
      setLinkedId(memoryItemIdOfHash(window.location.hash));
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    if (!linkedId || followedLink.current === linkedId) return;
    if (!openAreaOf(linkedId)) return;
    followedLink.current = linkedId;
    requestAnimationFrame(() =>
      document
        .getElementById(memoryItemAnchor(linkedId))
        ?.scrollIntoView({ block: "center" }),
    );
  }, [linkedId, openAreaOf]);

  useEffect(() => {
    if (!correctId || !correctLoaded || readOnly) return;
    openAreaOfRef.current(correctId);
    setEditingId(correctId);
    requestAnimationFrame(() =>
      document
        .getElementById(`minnespost-${correctId}`)
        ?.scrollIntoView({ block: "center" }),
    );
  }, [correctId, correctN, correctLoaded, readOnly]);

  // Every write refetches instead of patching local state. That is not
  // laziness: a revision returns a NEW row id and flips the old one to
  // `superseded`, so the list the coach is looking at is stale in two places
  // at once after a correction.
  const run = useCallback(
    async (action: () => Promise<unknown>, fallback: string) => {
      setBusy(true);
      setError(null);
      try {
        await action();
        await reload();
        onChanged?.();
        return true;
      } catch (e) {
        handleError(e, fallback);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [handleError, reload, onChanged],
  );

  if (forbidden) return null;

  // The persistent class is read by area; the other classes sit under
  // "Aktuellt". A row from an older server has no class and reads as
  // persistent.
  const classOf = (i: CoachMemoryItem): CoachMemoryClass =>
    i.memoryClass ?? defaultMemoryClass(i.category);
  const areas = (memory?.areas ?? []).map((a) => ({
    heading: CATEGORY_LABELS[a.category],
    key: a.category,
    items: a.items.filter((i) => classOf(i) === "persistent"),
  }));
  const filled = areas.filter((a) => a.items.length > 0);
  const visible = showEmptyAreas ? areas : filled;
  const currentGroups = CURRENT_MEMORY_CLASSES.map((c) => ({
    heading: CLASS_LABELS[c],
    key: c,
    items: (memory?.areas ?? [])
      .flatMap((a) => a.items)
      .filter((i) => classOf(i) === c),
  }));
  const currentFilled = currentGroups.filter((g) => g.items.length > 0);
  const upcoming = memory?.upcoming ?? [];
  const total = (memory?.areas ?? []).reduce((n, a) => n + a.items.length, 0);
  const history = memory?.history ?? [];
  const retired = retiredItems(history);
  const groupProps = {
    patientId,
    onRequestRegistered: () => setRequestsVersion((v) => v + 1),
    onUnauthorized: () => void signOut(),
    history,
    busy,
    editingId,
    retiringId,
    onEdit: setEditingId,
    onAskRetire: setRetiringId,
    onConfirm: (itemId: string) =>
      void run(
        () => confirmCoachMemoryItem(patientId, itemId),
        "Bekräftelsen kunde inte sparas.",
      ),
    onRevise: async (itemId: string, body: ReviseBody) => {
      const ok = await run(
        () => reviseCoachMemoryItem(patientId, itemId, body),
        "Rättelsen kunde inte sparas.",
      );
      if (ok) setEditingId(null);
    },
    onRetire: async (itemId: string) => {
      const ok = await run(
        () =>
          retireCoachMemoryItem(patientId, itemId, {
            lifecycle: "inactive",
          }),
        "Noteringen kunde inte inaktiveras.",
      );
      if (ok) setRetiringId(null);
    },
  };

  return (
    <section
      id={PATIENT_MEMORY_ANCHOR}
      className="scroll-mt-48 bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md"
    >
      <div className="flex flex-wrap items-center justify-between gap-sm">
        {/* "Patientminne", not "Patientbild". The domain calls this the
            patient picture (TES-745/746), but the level it now sits inside is
            ALSO called Patientbild — built in parallel, so neither side saw
            the collision. On screen it read as the same heading twice, one
            nested in the other, which makes the page look like it lost its
            place. The level keeps the domain word; the panel takes the one
            that says what it actually holds. */}
        <h2 className="font-display text-lg">Patientminne</h2>
        <div className="flex items-center gap-sm">
          {memory ? (
            <span className="text-xs text-textSecondary">
              {total} {total === 1 ? "notering" : "noteringar"}
            </span>
          ) : null}
          {readOnly ? null : (
            <button
              type="button"
              onClick={() => setAdding((v) => !v)}
              disabled={busy || !claims}
              className={BTN}
            >
              {adding ? "Avbryt" : "Lägg till"}
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-textSecondary">
        Varje område börjar med en kort sammanställning av sina rader. Öppna
        källorna för att se, bekräfta, rätta eller ta bort dem. Varje rad är ett
        eget påstående. En rättelse sparas som en ny version — den tidigare
        formuleringen och källan ligger kvar i historiken.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}

      {/* TES-1237 S3, TES-1367 — the patient's open requests, registered by
          her coach (renders nothing when none is open). */}
      <PatientMemoryRequests
        key={requestsVersion}
        patientId={patientId}
        onChanged={() => {
          void reload();
          onChanged?.();
        }}
        onUnauthorized={() => void signOut()}
      />

      {adding && claims && !readOnly ? (
        <AddForm
          busy={busy}
          onCancel={() => setAdding(false)}
          onSave={async (body) => {
            const ok = await run(
              () =>
                createCoachMemoryItem(patientId, {
                  ...body,
                  // A coach writing freehand files the claim against herself:
                  // there is no such thing as a memory item without
                  // provenance, and here the author IS the source.
                  sourceType: "coach_note",
                  sourceId: claims.sub,
                }),
              "Noteringen kunde inte sparas.",
            );
            if (ok) setAdding(false);
          }}
        />
      ) : null}

      {!memory && !error ? (
        <p className="text-sm text-textSecondary">Hämtar patientbilden…</p>
      ) : !memory ? null : total === 0 &&
        upcoming.length === 0 &&
        !showEmptyAreas ? (
        <p className="text-sm text-textSecondary">
          Inget noterat om patienten ännu. Det som sägs i chatt, incheckningar
          och möten hamnar här när det noteras.
        </p>
      ) : (
        <div className="flex flex-col gap-md">
          {currentFilled.length > 0 || showEmptyAreas ? (
            <div className="flex flex-col gap-sm">
              <h3 className="font-display text-base">Aktuellt</h3>
              {(showEmptyAreas ? currentGroups : currentFilled).map((g) => (
                <Group
                  key={g.key}
                  heading={g.heading}
                  items={g.items}
                  {...groupProps}
                />
              ))}
            </div>
          ) : null}
          {upcoming.length > 0 ? (
            <Group
              heading="Kommande tillfällig kontext"
              items={upcoming}
              {...groupProps}
            />
          ) : null}
          {filled.length > 0 || showEmptyAreas ? (
            <div className="flex flex-col gap-sm">
              <h3 className="font-display text-base">
                {CLASS_LABELS.persistent}
              </h3>
              {visible.map((area) => (
                <Group
                  key={area.key}
                  heading={area.heading}
                  items={area.items}
                  fold={{
                    open: openAreas.has(area.key),
                    onToggle: () =>
                      setOpenAreas((s) => {
                        const next = new Set(s);
                        if (!next.delete(area.key)) next.add(area.key);
                        return next;
                      }),
                  }}
                  {...groupProps}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}

      {retired.length > 0 ? (
        <details className="text-xs text-textSecondary">
          <summary className="cursor-pointer">
            Historik ({retired.length})
          </summary>
          <ul className="mt-xs flex flex-col gap-xs">
            {retired.map((item) => (
              <li key={item.id}>
                <HistoryRow item={item} />
                <PreviousVersions item={item} history={history} />
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {memory ? (
        <button
          type="button"
          onClick={() => setShowEmptyAreas((v) => !v)}
          className="self-start text-xs text-brand hover:opacity-80"
        >
          {showEmptyAreas
            ? "Dölj tomma områden"
            : `Visa alla ${COACH_MEMORY_CATEGORIES.length} områden`}
        </button>
      ) : null}
    </section>
  );
}

type ReviseBody = {
  body: string;
  category?: CoachMemoryCategory;
  memoryClass?: CoachMemoryClass;
  validFrom?: string;
  validUntil?: string;
  impact?: string;
  agreement?: string;
};

// One heading and its rows: an area of the persistent class, or a class under
// "Aktuellt". With `fold` (TES-1211, the areas) the heading is followed by the
// area's summary and the rows open behind "Visa källor".
function Group({
  fold,
  patientId,
  onRequestRegistered,
  onUnauthorized,
  heading,
  items,
  history,
  busy,
  editingId,
  retiringId,
  onEdit,
  onAskRetire,
  onConfirm,
  onRevise,
  onRetire,
}: {
  fold?: { open: boolean; onToggle: () => void };
  patientId: string;
  onRequestRegistered: () => void;
  onUnauthorized: () => void;
  heading: string;
  items: readonly CoachMemoryItem[];
  history: readonly CoachMemoryItem[];
  busy: boolean;
  editingId: string | null;
  retiringId: string | null;
  onEdit: (itemId: string | null) => void;
  onAskRetire: (itemId: string | null) => void;
  onConfirm: (itemId: string) => void;
  onRevise: (itemId: string, body: ReviseBody) => void;
  onRetire: (itemId: string) => void;
}) {
  return (
    <div>
      <h4 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
        {heading}
      </h4>
      {items.length === 0 ? (
        <p className="text-sm text-textSecondary">Inget noterat.</p>
      ) : (
        <>
          {fold ? (
            <AreaSummary
              items={items}
              open={fold.open}
              onToggle={fold.onToggle}
            />
          ) : null}
          {fold && !fold.open ? null : (
            <ul className="flex flex-col gap-xs">
              {items.map((item) => (
                <li key={item.id} id={`minnespost-${item.id}`}>
                  {editingId === item.id ? (
                    <ReviseForm
                      item={item}
                      busy={busy}
                      onCancel={() => onEdit(null)}
                      onSave={(body) => onRevise(item.id, body)}
                    />
                  ) : (
                    <MemoryRow
                      item={item}
                      busy={busy}
                      retiring={retiringId === item.id}
                      onEdit={() => onEdit(item.id)}
                      onAskRetire={() => onAskRetire(item.id)}
                      onCancelRetire={() => onAskRetire(null)}
                      onConfirm={() => onConfirm(item.id)}
                      onRetire={() => onRetire(item.id)}
                    />
                  )}
                  {/* TES-1367 — a request the patient made outside the app. */}
                  {editingId === item.id ? null : (
                    <div className="mt-xs">
                      <RegisterMemoryRequest
                        patientId={patientId}
                        itemId={item.id}
                        onRegistered={onRequestRegistered}
                        onUnauthorized={onUnauthorized}
                      />
                    </div>
                  )}
                  <PreviousVersions item={item} history={history} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// TES-1211 — what the area says, derived from its rows and nothing else; the
// rows are its sources.
function AreaSummary({
  items,
  open,
  onToggle,
}: {
  items: readonly CoachMemoryItem[];
  open: boolean;
  onToggle: () => void;
}) {
  const summary = summarizeMemoryArea(items);
  const unconfirmed = memoryAreaUnconfirmedLine(summary);
  return (
    <div className="mb-xs flex flex-col gap-[2px]">
      {summary.lead ? (
        <p className="text-sm text-textPrimary">{summary.lead.body}</p>
      ) : null}
      {unconfirmed ? (
        <p className="text-xs text-textSecondary">{unconfirmed}</p>
      ) : null}
      <p className="text-[11px] text-textSecondary">
        {memoryAreaFacts(summary)}
      </p>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="self-start text-xs text-brand hover:opacity-80"
      >
        {open ? "Dölj källor" : `Visa källor (${summary.count})`}
      </button>
    </div>
  );
}

function MemoryRow({
  item,
  busy,
  retiring,
  onEdit,
  onAskRetire,
  onCancelRetire,
  onConfirm,
  onRetire,
}: {
  item: CoachMemoryItem;
  busy: boolean;
  retiring: boolean;
  onEdit: () => void;
  onAskRetire: () => void;
  onCancelRetire: () => void;
  onConfirm: () => void;
  onRetire: () => void;
}) {
  const readOnly = useReadOnly();
  const hypothesis = item.factType === "ai_observation";
  const confirmed = item.confirmedAt !== null;
  // The verification state is carried by the whole row, not only by a chip:
  // scanning the panel has to answer "what do we actually know" without
  // reading every label.
  const frame = confirmed
    ? "border-success/40 bg-success/5"
    : hypothesis
      ? "border-warning/40 bg-warning/10"
      : "border-surfaceMuted";

  return (
    // TES-1074 — the anchor "Patienten i korthet" links each line to.
    <div
      id={memoryItemAnchor(item.id)}
      className={`scroll-mt-48 rounded-md border p-sm ${frame}`}
    >
      <div className="mb-xs flex flex-wrap items-center gap-xs">
        <span className="rounded-pill bg-surfaceMuted px-sm py-[1px] text-[11px] text-textSecondary">
          {FACT_TYPE_LABELS[item.factType]}
        </span>
        {confirmed ? (
          <span className="rounded-pill bg-success/15 px-sm py-[1px] text-[11px] text-textPrimary">
            Bekräftad {item.confirmedAt?.slice(0, 10)}
          </span>
        ) : hypothesis ? (
          <span className="rounded-pill bg-warning/20 px-sm py-[1px] text-[11px] text-textPrimary">
            Obekräftad — hypotes
          </span>
        ) : null}
        {item.supersedesId ? (
          <span className="text-[11px] text-textSecondary">rättad</span>
        ) : null}
      </div>

      <p className="text-sm text-textPrimary">{item.body}</p>
      <TemporaryContext item={item} />
      <Provenance item={item} />

      {readOnly ? null : (
        <div className="mt-xs flex flex-wrap items-center gap-xs">
          {hypothesis && !confirmed ? (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={BTN}
            >
              Bekräfta
            </button>
          ) : null}
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            className={BTN}
          >
            Rätta
          </button>
          {retiring ? (
            <>
              <span className="text-[11px] text-textSecondary">
                Raden inaktiveras och flyttas till historiken.
              </span>
              <button
                type="button"
                onClick={onRetire}
                disabled={busy}
                className={BTN}
              >
                Bekräfta inaktivering
              </button>
              <button
                type="button"
                onClick={onCancelRetire}
                disabled={busy}
                className={BTN}
              >
                Avbryt
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onAskRetire}
              disabled={busy}
              className={BTN}
            >
              Ta bort
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// TES-1019 — the row's origin, one line of labelled fields.
function Provenance({ item }: { item: CoachMemoryItem }) {
  return (
    <dl className="mt-xs flex flex-wrap gap-x-md gap-y-[2px] text-[11px] text-textSecondary">
      {provenanceFields(item).map((f) => (
        <div key={f.label} className="flex gap-[4px]">
          <dt>{f.label}:</dt>
          <dd className="text-textPrimary">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// TES-1192 — a temporary context's period, impact and agreement.
function TemporaryContext({ item }: { item: CoachMemoryItem }) {
  const fields = temporaryContextFields(item);
  if (fields.length === 0) return null;
  return (
    <dl className="mt-xs flex flex-col gap-[2px] text-xs text-textSecondary">
      {fields.map((f) => (
        <div key={f.label} className="flex gap-[4px]">
          <dt>{f.label}:</dt>
          <dd className="text-textPrimary">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// A row that is no longer current, read-only: retired, or a temporary
// context past its end date (TES-1192), shown with its period. A row removed
// with its source (the patient deleted the message) comes without its text,
// and so does one restricted on her request (TES-1367).
function HistoryRow({ item }: { item: CoachMemoryItem }) {
  return (
    <div className="rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-sm">
      <span className="text-[11px] text-textSecondary">
        {historyHeading(item)}
      </span>
      {item.body ? (
        <p className="text-sm text-textPrimary">{item.body}</p>
      ) : (
        <p className="text-sm text-textSecondary">
          {item.lifecycle === "restricted"
            ? "Texten visas inte, eftersom patienten har begärt begränsning."
            : "Texten visas inte, eftersom källan har tagits bort."}
        </p>
      )}
      {item.temporal === "expired" ? <TemporaryContext item={item} /> : null}
      <Provenance item={item} />
    </div>
  );
}

// "Tidigare versioner": the wordings and sources a row replaced.
function PreviousVersions({
  item,
  history,
}: {
  item: CoachMemoryItem;
  history: readonly CoachMemoryItem[];
}) {
  const versions = previousVersions(item, history);
  if (versions.length === 0) return null;
  return (
    <details className="mt-xs text-xs text-textSecondary">
      <summary className="cursor-pointer">
        Tidigare versioner ({versions.length})
      </summary>
      <ul className="mt-xs flex flex-col gap-xs">
        {versions.map((v) => (
          <li key={v.id}>
            <HistoryRow item={v} />
          </li>
        ))}
      </ul>
    </details>
  );
}

// TES-1192 — a temporary context's own fields: "vad som händer, start- och
// slutdatum när de är kända, möjlig påverkan på behandlingsvardagen,
// eventuell överenskommelse, om och när coachen ska följa upp" (A-01). What
// happens is the row's text; the rest is here. The follow-up date is offered
// only when the row is created.
type Period = {
  validFrom: string;
  validUntil: string;
  impact: string;
  agreement: string;
  followUpOn: string;
};

function PeriodFields({
  value,
  onChange,
  withFollowUp,
}: {
  value: Period;
  onChange: (next: Period) => void;
  withFollowUp: boolean;
}) {
  const set = (key: keyof Period) => (v: string) =>
    onChange({ ...value, [key]: v });
  return (
    <div className="flex flex-col gap-sm rounded-md bg-surfaceMuted/30 p-sm">
      <div className="flex flex-wrap gap-sm">
        <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
          Startdatum
          <input
            type="date"
            value={value.validFrom}
            onChange={(e) => set("validFrom")(e.target.value)}
            className={FIELD}
          />
        </label>
        <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
          Slutdatum
          <input
            type="date"
            required
            value={value.validUntil}
            min={value.validFrom || undefined}
            onChange={(e) => set("validUntil")(e.target.value)}
            className={FIELD}
          />
        </label>
        {withFollowUp ? (
          <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
            Följ upp den (valfritt)
            <input
              type="date"
              value={value.followUpOn}
              onChange={(e) => set("followUpOn")(e.target.value)}
              className={FIELD}
            />
          </label>
        ) : null}
      </div>
      <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
        Påverkan på behandlingsvardagen
        <textarea
          value={value.impact}
          onChange={(e) => set("impact")(e.target.value)}
          rows={2}
          maxLength={2000}
          className={`w-full ${FIELD}`}
        />
      </label>
      <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
        Eventuell överenskommelse
        <textarea
          value={value.agreement}
          onChange={(e) => set("agreement")(e.target.value)}
          rows={2}
          maxLength={2000}
          className={`w-full ${FIELD}`}
        />
      </label>
      <p className="text-[11px] text-textSecondary">
        Efter slutdatum lämnar posten Aktuellt men finns kvar i historiken.
      </p>
    </div>
  );
}

function ClassSelect({
  value,
  onChange,
}: {
  value: CoachMemoryClass;
  onChange: (c: CoachMemoryClass) => void;
}) {
  return (
    <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
      Klass
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as CoachMemoryClass)}
        className={FIELD}
      >
        {COACH_MEMORY_CLASSES.map((c) => (
          <option key={c} value={c}>
            {CLASS_LABELS[c]}
          </option>
        ))}
      </select>
    </label>
  );
}

const EMPTY_PERIOD: Period = {
  validFrom: "",
  validUntil: "",
  impact: "",
  agreement: "",
  followUpOn: "",
};

// The period's fields as the API takes them: empty dates are left out.
function periodBody(p: Period, withFollowUp: boolean) {
  return {
    ...(p.validFrom ? { validFrom: p.validFrom } : {}),
    ...(p.validUntil ? { validUntil: p.validUntil } : {}),
    impact: p.impact.trim(),
    agreement: p.agreement.trim(),
    ...(withFollowUp && p.followUpOn ? { followUpOn: p.followUpOn } : {}),
  };
}

function AddForm({
  busy,
  onCancel,
  onSave,
}: {
  busy: boolean;
  onCancel: () => void;
  onSave: (body: {
    category: CoachMemoryCategory;
    factType: CoachMemoryFactType;
    body: string;
    memoryClass: CoachMemoryClass;
    validFrom?: string;
    validUntil?: string;
    impact?: string;
    agreement?: string;
    followUpOn?: string;
  }) => void;
}) {
  const [category, setCategory] = useState<CoachMemoryCategory>("goal");
  const [factType, setFactType] =
    useState<CoachMemoryFactType>("patient_statement");
  const [body, setBody] = useState("");
  // The class follows the area until the coach picks one herself.
  const [chosenClass, setChosenClass] = useState<CoachMemoryClass | null>(null);
  const memoryClass = chosenClass ?? defaultMemoryClass(category);
  const temporary = memoryClass === "temporary_context";
  const [period, setPeriod] = useState<Period>(EMPTY_PERIOD);

  return (
    <div className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm">
      <div className="flex flex-wrap gap-sm">
        <ClassSelect value={memoryClass} onChange={setChosenClass} />
        <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
          Område
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as CoachMemoryCategory)}
            className={FIELD}
          >
            {COACH_MEMORY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
          Typ
          <select
            value={factType}
            onChange={(e) => setFactType(e.target.value as CoachMemoryFactType)}
            className={FIELD}
          >
            {AUTHORABLE_FACT_TYPES.map((f) => (
              <option key={f} value={f}>
                {FACT_TYPE_LABELS[f]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        maxLength={4000}
        className={`w-full ${FIELD}`}
        aria-label="Notering"
        placeholder="Ett påstående per rad — det patienten sa, eller det ni kom överens om."
      />
      {temporary ? (
        <PeriodFields value={period} onChange={setPeriod} withFollowUp />
      ) : null}
      <div className="flex items-center gap-sm">
        <button
          type="button"
          onClick={() =>
            onSave({
              category,
              factType,
              body: body.trim(),
              memoryClass,
              ...(temporary ? periodBody(period, true) : {}),
            })
          }
          disabled={busy || !body.trim() || (temporary && !period.validUntil)}
          className={BTN_PRIMARY}
        >
          {busy ? "Sparar…" : "Spara"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={BTN}
        >
          Avbryt
        </button>
      </div>
    </div>
  );
}

function ReviseForm({
  item,
  busy,
  onCancel,
  onSave,
}: {
  item: CoachMemoryItem;
  busy: boolean;
  onCancel: () => void;
  onSave: (body: ReviseBody) => void;
}) {
  const [body, setBody] = useState(item.body);
  const [category, setCategory] = useState<CoachMemoryCategory>(item.category);
  const initialClass = item.memoryClass ?? defaultMemoryClass(item.category);
  const [memoryClass, setMemoryClass] =
    useState<CoachMemoryClass>(initialClass);
  const temporary = memoryClass === "temporary_context";
  const [period, setPeriod] = useState<Period>({
    validFrom: item.validFrom ?? "",
    validUntil: item.validUntil ?? "",
    impact: item.impact ?? "",
    agreement: item.agreement ?? "",
    followUpOn: "",
  });

  return (
    <div className="flex flex-col gap-sm rounded-md border border-brand/40 p-sm">
      <p className="text-[11px] text-textSecondary">
        Rättelsen sparas som en ny version. Den nuvarande formuleringen tas inte
        bort — den flyttas till historiken. Typ och källa behålls.
      </p>
      <ClassSelect value={memoryClass} onChange={setMemoryClass} />
      <label className="flex flex-col gap-[2px] text-xs text-textSecondary">
        Område
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as CoachMemoryCategory)}
          className={FIELD}
        >
          {COACH_MEMORY_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        maxLength={4000}
        className={`w-full ${FIELD}`}
        aria-label="Rätta noteringen"
      />
      {temporary ? (
        <PeriodFields
          value={period}
          onChange={setPeriod}
          withFollowUp={false}
        />
      ) : null}
      <div className="flex items-center gap-sm">
        <button
          type="button"
          onClick={() =>
            onSave({
              body: body.trim(),
              category: category === item.category ? undefined : category,
              memoryClass:
                memoryClass === initialClass ? undefined : memoryClass,
              ...(temporary ? periodBody(period, false) : {}),
            })
          }
          disabled={busy || !body.trim() || (temporary && !period.validUntil)}
          className={BTN_PRIMARY}
        >
          {busy ? "Sparar…" : "Spara rättelse"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={BTN}
        >
          Avbryt
        </button>
      </div>
    </div>
  );
}
